/* eslint-disable no-useless-computed-key */
import { h } from "preact";
import { useEffect, useState, useContext, useRef } from "preact/hooks";

import { init } from "../lib/pell";
import { useUser, useFirestore } from "../lib/preactfire";
import {
  Context,
  setUserInfoAction,
  setLoginModalVisible,
} from "../lib/globalState";
import {
  clearInput,
  disablePellEditor,
  enablePellEditor,
  removeOverlay,
  stripTags,
} from "../lib/helper";

export default function CommentsTabContent() {
  const user = useUser();
  const db = useFirestore();

  const { state, dispatch } = useContext(Context);

  const [comment, setComment] = useState(null);
  const [enable, setEnable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [isHover, setHover] = useState(false);

  const editor = useRef();

  useEffect(() => {
    init({
      element: document.getElementById("comment-editor"),
      defaultParagraphSeparator: "p",
      placeholder: "Add your comment",
      actions: [
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "link",
        "quote",
        "ulist",
      ],
      onChange: setComment,
    });
  }, []);

  const onSubmit = async () => {
    if (comment && stripTags(comment).length > 0) {
      if (user) {
        const { creationTime, lastSignInTime } = user.metadata;

        disablePellEditor(editor.current);
        setEnable(false);

        let response = await fetch(
          "https://api.ipinfodb.com/v3/ip-city/?key=25864308b6a77fd90f8bf04b3021a48c1f2fb302a676dd3809054bc1b07f5b42&format=json"
        );
        const {
          regionName: geo_region,
          countryName: geo_country,
          cityName: geo_city,
          ipAddress: ip_address,
        } = await response.json();

        const location = {
          geo_region,
          geo_country,
          geo_city,
          ip_address,
          geo_language: "",
        };

        // Find device type
        let device;
        if (window.innerWidth > 768) {
          device = "Desktop";
        } else if (window.innerWidth < 767 && window.innerWidth > 481) {
          device = "iPad/Tablet";
        } else if (window.innerWidth < 480) {
          device = "Mobile";
        }

        // User Agent, Device, OS and Screen Resolution
        const browser = window.navigator.appCodeName;
        const operating_system = window.navigator.platform;
        const device_type = device;
        const device_name = window.navigator.platform;
        const screen_res = `${screen.width}x${screen.height}`;
        const technology = {
          ["tech_browser"]: browser,
          ["tech_os"]: operating_system,
          ["tech_device_type"]: device_type,
          ["tech_device_name"]: device_name,
          ["tech_screen_res"]: screen_res,
        };
        const user_id = user.uid;
        const display_name = user.displayName;
        const profile_img = user.photoURL;
        const timestamp = Date.now();

        const type = "comment";
        const checkData = {
          comment: {
            text: comment,
          },
          requestedAttributes: {
            TOXICITY: {},
          },
        };

        const docRef = db.collection("comment-user").doc(user.uid);
        const doc = await docRef.get();

        if (doc.exists || user) {
          const total_comments = doc.data()?.total_comments ?? 0;
          const hidden_comments = doc.data()?.hidden_comments ?? 0;

          response = await fetch(
            "https://commentanalyzer.googleapis.com/v1alpha1/comments:analyze?key=AIzaSyBrQhnYKisBxAYAhFc3DiygTWhoSYd2PiM",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(checkData),
            }
          );
          const result = await response.json();
          const toxicity =
            Math.round(
              result.attributeScores.TOXICITY.summaryScore.value * 100
            ) / 100;
          const language = result.languages["0"];

          let status = "public";
          if (toxicity <= 0.6) {
            const docRef = db.collection("comment").doc(state.apiKey);
            const doc = await docRef.get();

            if (doc.exists) {
              if (doc.data().restricted_word) {
                const reply_value_array = comment.split(" ");

                let isReview = false;
                for (let i = 0; i < doc.data().restricted_word.length; i++) {
                  for (let j = 0; j < reply_value_array.length; j++) {
                    if (
                      doc.data().restricted_word[i] ==
                      reply_value_array[j].toLowerCase()
                    ) {
                      isReview = true;
                    }
                  }
                }
                if (isReview) {
                  status = "review";
                }
              }
            }
          } else if (toxicity > 0.6 && toxicity <= 0.85) {
            status = "review";
          } else {
            status = "hidden";
            setLoading(true);
            setLoadingMessage("Your comments have objectionable words");
            setTimeout(() => {
              clearInput(editor.current);
              enablePellEditor(editor.current);
              setEnable(true);
              setLoading(false);
            }, 3000);
            return;
          }

          setLoading(true);
          setLoadingMessage("Submitting your comment...");

          try {
            await db
              .collection("comment")
              .doc()
              .set({
                api_key: state.apiKey,
                image:
                  "https://s3.amazonaws.com/media-p.slid.es/uploads/429997/images/2111824/negativespace-161.jpg",
                language,
                page: "http://demo.goemotify.com/post1.html",
                status,
                story_id: "90801", // Need to discuss
                content: comment,
                timestamp,
                title:
                  "Man must explore, and this is exploration at its greatest",
                toxicity,
                type,
                user_id,
                user_name: display_name,
                profile_img,
                ...location,
                ...technology,
              });

            await db
              .collection("comment-user")
              .doc(user.uid)
              .set({
                user_id: user.uid,
                first_name: user.displayName.substr(
                  0,
                  user.displayName.lastIndexOf(" ")
                ),
                last_name:
                  user.displayName.substr(
                    user.displayName.lastIndexOf(" ") + 1
                  ) ?? "",
                email: user.email,
                phone_no: user.phoneNumber,
                profile_img: user.photoURL,
                registered_at: creationTime,
                last_seen: lastSignInTime,
                login_method: user.providerData[0].providerId,
                registered_ip: ip_address,
                ...location,
                total_comments: total_comments + 1,
                hidden_comments:
                  status === "hidden" ? hidden_comments + 1 : hidden_comments,
                total_flags: 0,
                risk_profile: "",
                blocked_by: "",
              });

            setLoadingMessage("You comment posted successfully!");
            setComment("");
            clearInput(editor.current);

            setTimeout(() => {
              enablePellEditor(editor.current);
              setEnable(true);
              setLoading(false);
            }, 3000);
          } catch (e) {
            console.log(e);
          }
        }
      } else {
        dispatch(setLoginModalVisible(true));
      }
    }
  };

  return (
    <div>
      <div className="list" id="comments">
        <div className="comment-box no-shadow">
          <div className="comment-box-content">
            <div
              ref={editor}
              id="comment-editor"
              className="comment-editor unfocused"
              onClick={(e) => {
                e.currentTarget.classList.remove("unfocused");
                document
                  .querySelector(".active-actions")
                  .classList.add("active-editor");
                document.querySelectorAll(".comment-box").forEach((el) => {
                  el.classList.remove("no-shadow");
                  document
                    .querySelector(".comment-action-container")
                    .classList.add("show-comment-actions");
                });
                editor.current.querySelector(".pell-content").focus();
              }}
            />
            <div className="active-actions">
              <span id="comment-length-count">
                {stripTags(comment)?.length > 0 && stripTags(comment)?.length}
              </span>
              <span className="unfocused-text-icon">
                <img src={require("../assets/images/text.svg")} height="16" />
              </span>
            </div>
          </div>

          <div className="comment-action-container">
            <div className="comment-login-action" id="comment-login-prompt">
              {user ? (
                <div
                  className="comment-box-user"
                  onClick={() => {
                    removeOverlay();
                    dispatch(
                      setUserInfoAction(
                        {
                          name: user.displayName,
                          profile_img: user.photoURL,
                          replies: {},
                          user_id: user.uid,
                        },
                        true
                      )
                    );
                  }}
                >
                  <img src={user.photoURL} className="comment-box-user-img" />
                  <span
                    className="comment-box-user-name"
                    style={`color: ${state.color}`}
                  >
                    {user.displayName}
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  className="login-modal comment-login"
                  onClick={() => {
                    removeOverlay();
                    dispatch(setLoginModalVisible(true));
                  }}
                  onMouseOver={() => setHover(true)}
                  onMouseOut={() => setHover(false)}
                >
                  <span style={isHover && `color: ${state.color}`}>
                    Log in to comment
                  </span>
                </button>
              )}
            </div>
            <div className="comment-submit-section">
              <button
                disabled={!enable}
                className="comment-submission"
                style={`background-color: ${state.color}; border-color: ${state.color}`}
                onClick={onSubmit}
              >
                Submit
              </button>
            </div>
          </div>

          <div
            id="comments_submit"
            className="comment-submit-alert"
            style="display:none"
          >
            Posting your comment!
          </div>
        </div>

        {loading && (
          <div
            className={`alert ${
              loadingMessage.includes("objectionable") ? "error" : "success"
            }`}
          >
            <div className="message">{loadingMessage}</div>
          </div>
        )}
      </div>
      <div className="list hide" id="facebook">
        Facebook feed appears here
      </div>
      <div className="list hide" id="twitter">
        Twitter feed appears here
      </div>
    </div>
  );
}
